import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Navbar } from "../components/Navbar";
import api from "../lib/api";
import { getIcon } from "../lib/icons";
import { Progress } from "../components/ui/progress";
import { useAuth } from "../context/AuthContext";
import {
  ArrowRight, CheckCircle2, Target, Award, TrendingUp, Loader2, Lock, Clock, BookOpen, Rocket,
} from "lucide-react";

export default function Dashboard() {
  const { user } = useAuth();
  const [academies, setAcademies] = useState([]);
  const [progress, setProgress] = useState(null);
  const [badgeDefs, setBadgeDefs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([api.get("/academies"), api.get("/progress"), api.get("/badges")])
      .then(([a, p, b]) => {
        setAcademies(a.data);
        setProgress(p.data);
        setBadgeDefs(b.data || []);
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading || !progress)
    return (
      <div className="min-h-screen">
        <Navbar />
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </div>
    );

  const completed = progress.completed_lessons || [];
  const academyLessonIds = (a) => a.modules.flatMap((m) => m.lesson_ids || []);
  const academyProgress = (a) => {
    const ids = academyLessonIds(a);
    const done = ids.filter((id) => completed.includes(id)).length;
    return { done, total: ids.length, pct: ids.length ? Math.round((done / ids.length) * 100) : 0 };
  };

  const quizAvg = (() => {
    const vals = Object.values(progress.quiz_scores || {});
    if (!vals.length) return 0;
    return Math.round(vals.reduce((a, b) => a + b.percent, 0) / vals.length);
  })();

  const earnedBadges = new Set(progress.earned_badges || []);
  const badgeAwards = progress.badge_awards || {};

  const stats = [
    { label: "Lessons Complete", value: `${progress.lessons_completed_count}/${progress.total_lessons}`, icon: CheckCircle2 },
    { label: "Overall Progress", value: `${progress.overall_percent}%`, icon: TrendingUp },
    { label: "Avg Quiz Score", value: `${quizAvg}%`, icon: Target },
    { label: "Badges Earned", value: `${progress.earned_badges.length}`, icon: Award },
  ];

  return (
    <div className="min-h-screen pb-16">
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 pt-8">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
          <span className="text-xs font-mono uppercase tracking-[0.3em] text-primary">Flight Deck</span>
          <h1 className="font-display text-3xl md:text-4xl font-bold mt-2">
            {completed.length === 0 ? "Welcome aboard" : "Welcome back"}, {user?.name?.split(" ")[0]}.
          </h1>
          <p className="text-muted-foreground mt-2 text-sm md:text-base">
            {completed.length === 0
              ? "Your professional drone training starts here. Mission One takes about 9 minutes."
              : "Your professional drone training ecosystem. Pick up where you left off."}
          </p>
          <Link to="/module/gs-welcome" data-testid="start-mission-one-btn"
            className="inline-flex items-center gap-2 mt-5 h-12 px-6 rounded-sm bg-primary text-primary-foreground font-semibold hover:bg-primary/90 transition-colors glow-cyan">
            {completed.length === 0 ? "Start Mission One" : "Resume Training"}
            <ArrowRight className="h-4 w-4" />
          </Link>
        </motion.div>

        {!completed.includes("gsw-1") && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
            className="mb-8 p-5 rounded-sm border border-accent/40 bg-accent/5 flex items-center justify-between gap-4 flex-wrap"
            data-testid="finish-lesson1-banner">
            <div className="flex items-center gap-3">
              <Rocket className="h-6 w-6 text-accent shrink-0" />
              <div>
                <div className="font-display font-bold">You're one mission from your first badge.</div>
                <p className="text-sm text-slate-400">Finish Lesson 1 to earn <span className="text-primary font-medium">First Flight</span> and start your path to Golden Five Graduate.</p>
              </div>
            </div>
            <Link to="/module/gs-welcome" data-testid="finish-lesson1-cta"
              className="inline-flex items-center gap-2 h-11 px-5 rounded-sm bg-accent text-accent-foreground font-semibold hover:bg-accent/90 transition-colors whitespace-nowrap">
              Finish your first mission <ArrowRight className="h-4 w-4" />
            </Link>
          </motion.div>
        )}

        {(() => {
          const gf = ["gsw-1", "gsw-2", "gsw-3", "gsw-4", "gsw-5"];
          const done = gf.filter((id) => completed.includes(id)).length;
          if (done === 0 || done === 5) return null;
          return (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
              className="mb-8 p-5 rounded-sm border border-primary/40 bg-primary/5" data-testid="golden-five-tracker">
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-3">
                  <Award className="h-6 w-6 text-primary shrink-0" />
                  <div>
                    <div className="font-display font-bold">You're {done} of 5 to <span className="text-primary">Golden Five Graduate</span>.</div>
                    <p className="text-sm text-slate-400">Finish {5 - done} more lesson{5 - done === 1 ? "" : "s"} to earn the badge.</p>
                  </div>
                </div>
                <Link to="/module/gs-welcome" data-testid="golden-five-tracker-cta"
                  className="inline-flex items-center gap-2 h-11 px-5 rounded-sm bg-primary text-primary-foreground font-semibold hover:bg-primary/90 transition-colors whitespace-nowrap">
                  Continue <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
              <div className="mt-4 flex gap-1.5">
                {gf.map((id) => (
                  <div key={id} className={`h-1.5 flex-1 rounded-full ${completed.includes(id) ? "bg-primary" : "bg-secondary"}`} />
                ))}
              </div>
            </motion.div>
          );
        })()}

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
          {stats.map((s, i) => (
            <motion.div key={s.label} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }} className="p-5 rounded-sm border border-border/70 bg-card"
              data-testid={`stat-${i}`}>
              <s.icon className="h-5 w-5 text-primary mb-4" />
              <div className="font-mono text-2xl font-bold">{s.value}</div>
              <div className="text-xs uppercase tracking-widest text-muted-foreground mt-1">{s.label}</div>
            </motion.div>
          ))}
        </div>

        {badgeDefs.length > 0 && (
          <div className="mb-10" data-testid="dashboard-achievements">
            <div className="flex items-baseline justify-between mb-4">
              <h2 className="font-display text-2xl font-bold flex items-center gap-2">
                <Award className="h-6 w-6 text-primary" /> Achievements
              </h2>
              <span className="text-xs font-mono uppercase tracking-widest text-muted-foreground">
                {earnedBadges.size}/{badgeDefs.length} earned
              </span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {badgeDefs.map((b) => {
                const Icon = getIcon(b.icon);
                const has = earnedBadges.has(b.id);
                return (
                  <div key={b.id} data-testid={`dashboard-badge-${b.id}`}
                    className={`p-4 rounded-sm border text-center transition-colors ${
                      has ? "border-primary/40 bg-primary/5" : "border-border/60 bg-card opacity-50"}`}>
                    <Icon className={`h-7 w-7 mx-auto mb-2 ${has ? "text-primary" : "text-muted-foreground"}`} />
                    <div className="text-xs font-medium leading-tight">{b.name}</div>
                    {has && badgeAwards[b.id] ? (
                      <div className="text-[9px] font-mono uppercase tracking-widest text-primary/80 mt-2">
                        Earned {new Date(badgeAwards[b.id]).toLocaleDateString()}
                      </div>
                    ) : (
                      <div className="text-[10px] text-muted-foreground mt-1.5 leading-tight line-clamp-2">{b.desc}</div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="flex items-baseline justify-between mb-5">
          <h2 className="font-display text-2xl font-bold">The Academies</h2>
          <span className="text-xs font-mono uppercase tracking-widest text-muted-foreground">
            {academies.length} tracks
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="academies-grid">
          {academies.map((a, i) => {
            const Icon = getIcon(a.icon);
            const ap = academyProgress(a);
            const soon = a.coming_soon || a.module_count === 0;
            const complete = !soon && ap.pct === 100;
            const CardInner = (
              <div className={`block group rounded-sm border bg-card overflow-hidden h-full transition-colors ${
                soon ? "border-border/50 opacity-90" : "border-border/70 hover:border-primary/50"}`}>
                <div className="relative h-40 overflow-hidden">
                  <img src={a.thumbnail} alt={a.title}
                    className={`w-full h-full object-cover transition-transform duration-500 ${soon ? "grayscale" : "group-hover:scale-105"}`} />
                  <div className="absolute inset-0 bg-gradient-to-t from-card via-card/40 to-transparent pointer-events-none" />
                  <span className="absolute top-3 left-3 text-2xl" aria-hidden>{a.emoji}</span>
                  {soon ? (
                    <span className="absolute top-3 right-3 text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded-sm bg-background/80 text-muted-foreground border border-border"
                      data-testid={`academy-soon-${a.slug}`}>
                      <Clock className="h-3 w-3 inline mr-1" /> Launching soon
                    </span>
                  ) : complete ? (
                    <span className="absolute top-3 right-3"><CheckCircle2 className="h-5 w-5 text-primary" /></span>
                  ) : null}
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-2.5 mb-2">
                    <Icon className="h-5 w-5 text-primary shrink-0" />
                    <h3 className="font-display font-bold text-lg leading-tight">{a.title}</h3>
                  </div>
                  <p className="text-sm text-slate-400 leading-relaxed mb-4 line-clamp-2">{a.tagline}</p>
                  {soon ? (
                    <div className="flex items-center justify-between text-xs font-mono text-muted-foreground">
                      <span className="flex items-center gap-1.5"><BookOpen className="h-3.5 w-3.5" /> Included in your membership</span>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center justify-between text-xs font-mono text-muted-foreground mb-2">
                        <span>{a.module_count} modules · {a.lesson_count} lessons</span>
                        <span className="text-primary">{ap.pct}%</span>
                      </div>
                      <Progress value={ap.pct} className="h-1.5 bg-secondary" />
                      <div className="flex items-center justify-end text-xs font-mono text-primary mt-3 gap-1 opacity-100 lg:opacity-0 lg:group-hover:opacity-100 transition-opacity">
                        Enter academy <ArrowRight className="h-3.5 w-3.5" />
                      </div>
                    </>
                  )}
                </div>
              </div>
            );
            return (
              <motion.div key={a.slug} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 + i * 0.06 }} data-testid={`academy-card-${a.slug}`}>
                {soon ? CardInner : <Link to={`/academy/${a.slug}`}>{CardInner}</Link>}
              </motion.div>
            );
          })}
        </div>
      </main>
    </div>
  );
}
